@file:OptIn(ExperimentalResourceApi::class)

package ui.dashboard

import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.vector.ImageVector
import com.netguru.multiplatform.charts.pie.PieChartData
import common.frontend.api.Io
import common.frontend.api.IoReport
import compose.assets.Svg
import compose.assets.svg.*
import org.jetbrains.compose.resources.ExperimentalResourceApi
import org.jetbrains.compose.resources.StringResource
import org.ssoft.common.type.DashboardType
import provider.*
import ssoft.frontend.generated.resources.*
import theme.*
import tk.mallumo.compose.navigation.LocalNavigation
import tk.mallumo.compose.navigation.LocalNavigationArgs
import tk.mallumo.compose.navigation.Navigation
import tk.mallumo.compose.navigation.viewmodel.viewModel
import tools.EFieldList
import tools.roundBytes
import ui.dashboard.DashboardScope.Chart.Companion.pickColor
import ui.dashboard.DashboardScope.Chart.Field


interface DashboardScope {

    val vm: DashboardVM
    val action: (DashboardVM.Action) -> Unit
    val nav: Navigation
    val login: Io.User
    val args: ArgsDashboard
    val display: CommonDisplayProvider
    val homeDrawerProvider: HomeDrawerProvider

    companion object {

        @Composable
        fun rememberScope(): DashboardScope {
            val account = LocalAccountProvider.current
            val nav = LocalNavigation.current
            val vm = nav.viewModel(DashboardVM::class)
            val action = DashboardVM.Action.rememberAction(vm)
            val args = LocalNavigationArgs.current.rememberArgs(ArgsDashboard::class)
            val display = LocalDisplayProvider.current
            val homeDrawerProvider = LocalHomeDrawerProvider.current
            return remember {
                object : DashboardScope {
                    override val vm: DashboardVM
                        get() = vm
                    override val action: (DashboardVM.Action) -> Unit
                        get() = action
                    override val nav: Navigation
                        get() = nav
                    override val login: Io.User
                        get() = account.current!!
                    override val args: ArgsDashboard
                        get() = args
                    override val display: CommonDisplayProvider
                        get() = display
                    override val homeDrawerProvider: HomeDrawerProvider
                        get() = homeDrawerProvider

                }.also {
                    vm.init(it)
                }
            }
        }
    }

    class Wrapper(
        val data: IoReport.Dashboard,
        charts: List<DashboardCard>
    ) : EFieldList<DashboardCard, Wrapper.Item>(
        items = charts.toMutableList(),
        onBindEntry = { Item(it) }
    ) {

        class Item(
            override val data: DashboardCard,
            override val key: String = data.type.toString(),
        ) : Entry<DashboardCard>


        companion object {
            suspend fun make(user: Io.User, data: IoReport.Dashboard, modeCompany: Boolean): List<DashboardCard> {
                return user.dashboards.mapNotNull {
                    when (DashboardType[it]) {
                        DashboardType.CAMPAIGN_GLOBAL -> {
                            Chart.build(
                                icon = Svg.Animation_play,
                                type = DashboardType[it],
                                title = Res.string.dashboard__campaign_title,
                                subtitle = Res.string.dashboard__campaign_subtitle_global,
                                Res.string.dashboard__campaign_field_active to data.campaign.counter.global.active,
                                Res.string.dashboard__campaign_field_published to data.campaign.counter.global.published,

                                Res.string.dashboard__campaign_field_draft to data.campaign.counter.global.draft,
                                Res.string.dashboard__campaign_field_pause to data.campaign.counter.global.paused,
                                Res.string.dashboard__campaign_field_stop to data.campaign.counter.global.stop,

                                Res.string.dashboard__campaign_field_finish to data.campaign.counter.global.finished,
                            )
                        }

                        DashboardType.CAMPAIGN_TODAY -> {
                            Chart.build(
                                icon = Svg.Calendar_today,
                                DashboardType[it],
                                Res.string.dashboard__campaign_title,
                                Res.string.dashboard__campaign_subtitle_today,

                                Res.string.dashboard__campaign_field_published to data.campaign.counter.today.published,
                                Res.string.dashboard__campaign_field_will_winish_today to data.campaign.counter.today.willFinish,

                                Res.string.dashboard__campaign_field_start to data.campaign.counter.today.started,
                                Res.string.dashboard__campaign_field_draft to data.campaign.counter.today.draft,
                                Res.string.dashboard__campaign_field_pause to data.campaign.counter.today.paused,
                                Res.string.dashboard__campaign_field_stop to data.campaign.counter.today.stop,

                                Res.string.dashboard__campaign_field_edit to data.campaign.counter.today.edited,
                                Res.string.dashboard__campaign_field_delete to data.campaign.counter.today.deleted
                            )
                        }

                        DashboardType.DEVICE_LAYER_COUNTER -> {
                            Chart(
                                icon = Svg.Layers,
                                type = DashboardType.DEVICE_LAYER_COUNTER,
                                title = Res.string.dashboard__device_title,
                                subtitle = Res.string.dashboard__device_layers,
                                fields = listOf(
                                    Field(
                                        label = Res.string.dashboard__device_field_count,
                                        value = data.device.counter.count.toString(),
                                        color = Color.White,
                                        showInChart = false
                                    ),
                                    Field(
                                        label = Res.string.dashboard__device_field_layer_top,
                                        value = data.device.counter.layerTop.toString(),
                                        color = Color.BlueA400
                                    ),
                                    Field(
                                        label = Res.string.dashboard__device_field_layer_bottom,
                                        value = data.device.counter.layerBottom.toString(),
                                        color = Color.GreenA400
                                    ),
                                    Field(
                                        label = Res.string.dashboard__device_field_layer_both,
                                        value = data.device.counter.layerBoth.toString(),
                                        color = Color.DeepPurpleA400
                                    )
                                )
                            )
                        }

                        DashboardType.NONE -> null

                        DashboardType.PROBLEMS_DEVICE -> {
                            Message(
                                icon = Svg.Device,
                                type = DashboardType.PROBLEMS_DEVICE,
                                title = Res.string.dashboard__problems_device,
                                messages = data.device.messages
                            )
                        }

                        DashboardType.PROBLEMS_CAMPAIGN -> {
                            Message(
                                icon = Svg.Animation_play,
                                type = DashboardType.PROBLEMS_CAMPAIGN,
                                title = Res.string.dashboard__problems_campaign,
                                messages = data.campaign.messages
                            )
                        }

                        DashboardType.COMPANY_STORAGE -> {
                            Chart(
                                icon = Svg.Database,
                                type = DashboardType.COMPANY_STORAGE,
                                title = Res.string.dashboard__company_title,
                                subtitle = Res.string.dashboard__company_storage,
                                fields = buildList {
                                    if (modeCompany) {
                                        add(
                                            Field(
                                                label = Res.string.dashboard__company_storage_field_total,
                                                value = data.company.counter.storageTotal.toString(),
                                                valuePreview = data.company.counter.storageTotal.roundBytes() ?: "0 MB",
                                                color = Color.White,
                                                showInChart = false
                                            )
                                        )
                                        add(
                                            Field(
                                                label = Res.string.dashboard__company_storage_field_free,
                                                value = data.company.counter.storageFree.toString(),
                                                color = pickColor(Res.string.dashboard__company_storage_field_free),
                                                valuePreview = data.company.counter.storageFree.roundBytes() ?: "0 MB"
                                            )
                                        )
                                    }
                                    add(
                                        Field(
                                            label = Res.string.dashboard__company_storage_field_used,
                                            value = data.company.counter.storageUsed.toString(),
                                            color = pickColor(Res.string.dashboard__company_storage_field_used),
                                            valuePreview = data.company.counter.storageUsed.roundBytes() ?: "0 MB",
                                        )
                                    )
                                    add(
                                        Field(
                                            label = Res.string.dashboard__company_storage_field_unused,
                                            value = data.company.counter.storageUnused.toString(),
                                            color = pickColor(Res.string.dashboard__company_storage_field_unused),
                                            valuePreview = data.company.counter.storageUnused.roundBytes() ?: "0 MB",
                                        )
                                    )
                                    add(
                                        Field(
                                            label = Res.string.dashboard__company_storage_field_trash,
                                            value = data.company.counter.storageTrash.toString(),
                                            color = pickColor(Res.string.dashboard__company_storage_field_trash),
                                            valuePreview = data.company.counter.storageTrash.roundBytes() ?: "0 MB",
                                        )
                                    )
                                }
                            )
                        }
                    }
                }
            }
        }

    }

    sealed interface DashboardCard {
        val type: DashboardType
    }

    class Message(
        val icon: ImageVector,
        override val type: DashboardType,
        val title: StringResource,
        val messages: List<IoReport.Dashboard.Message>
    ) : DashboardCard

    class Chart(
        val icon: ImageVector,
        override val type: DashboardType,
        val title: StringResource,
        val subtitle: StringResource,
        val fields: List<Field>
    ) : DashboardCard {
        class Field(
            val label: StringResource,
            val value: String,
            val color: Color,
            val showInChart: Boolean = true,
            val valuePreview: String = value,
        ) {
            fun toPieChartData(): PieChartData = PieChartData(
                name = label.key,
                value = value.toDouble(),
                color = color
            )
        }

        companion object {
            fun pickColor(type: StringResource): Color = when (type) {
                Res.string.dashboard__campaign_field_active,
                Res.string.dashboard__company_storage_field_used,
                Res.string.dashboard__device_field_disconnected -> Color.Blue500

                Res.string.dashboard__campaign_field_published,
                Res.string.dashboard__device_field_low_storage -> Color.Indigo500

                Res.string.dashboard__campaign_field_will_winish_today,
                Res.string.dashboard__campaign_field_will_winish_week,
                Res.string.dashboard__campaign_field_will_winish_month,
                Res.string.dashboard__device_field_overheating -> Color.DeepPurple500

                Res.string.dashboard__campaign_field_start,
                Res.string.dashboard__device_field_display_off,
                Res.string.dashboard__company_storage_field_free,
                Res.string.dashboard__device_field_update_required -> Color.Teal500

                Res.string.dashboard__campaign_field_draft,
                Res.string.dashboard__company_storage_field_unused,
                Res.string.dashboard__device_field_no_apps -> Color.DeepOrangeA100

                Res.string.dashboard__campaign_field_pause,
                Res.string.dashboard__device_field_no_campaigns -> Color.Amber700

                Res.string.dashboard__campaign_field_stop,
                Res.string.dashboard__device_field_campaigns_without_impressions -> Color.Orange900

                Res.string.dashboard__campaign_field_edit -> Color.Brown500
                Res.string.dashboard__company_storage_field_total,
                Res.string.dashboard__campaign_field_finish -> Color.Grey300

                Res.string.dashboard__company_storage_field_trash,
                Res.string.dashboard__campaign_field_delete -> Color.BlueGrey600

                else -> Color.Black
            }

            suspend fun build(
                icon: ImageVector,
                type: DashboardType,
                title: StringResource,
                subtitle: StringResource,
                vararg pair: Pair<StringResource, Int>
            ): Chart {

                return Chart(
                    icon = icon,
                    type = type,
                    title = title,
                    subtitle = subtitle,
                    fields = pair.map {
                        Field(
                            label = it.first,
                            value = it.second.toString(),
                            color = pickColor(it.first)
                        )
                    }
                )
            }
        }
    }
}
